"use client"

import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import Link from "next/link"

export function ArticleBody({ content }: { content: string }) {
  return (
    <article className="text-base leading-8 text-[var(--foreground)] font-[family-name:var(--font-sans)] article-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => (
            <h2 className="text-2xl font-semibold mt-12 mb-4 tracking-tight">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-xl font-semibold mt-10 mb-3 tracking-tight">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="mb-6">{children}</p>
          ),
          a: ({ href, children }) => {
            if (href?.startsWith("/")) {
              return (
                <Link
                  href={href}
                  className="text-[var(--primary)] underline underline-offset-2 decoration-[var(--primary)]/40 hover:decoration-[var(--primary)] transition-colors"
                >
                  {children}
                </Link>
              )
            }
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--primary)] underline underline-offset-2 decoration-[var(--primary)]/40 hover:decoration-[var(--primary)] transition-colors"
              >
                {children}
              </a>
            )
          },
          img: ({ src, alt }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={src}
              alt={alt ?? ""}
              className="w-full rounded-xl my-8 object-cover"
            />
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-[var(--primary)] pl-5 my-8 italic text-[var(--muted-foreground)]">
              {children}
            </blockquote>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-6 mb-6 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-6 mb-6 space-y-1">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-7">{children}</li>
          ),
          hr: () => (
            <hr className="my-10 border-[var(--border)]" />
          ),
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => (
            <em>{children}</em>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  )
}
